import { useEffect, useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Info,
  Loader2,
  Package,
  Search,
  TrendingUp,
} from 'lucide-react';
import {
  getCommodityBidAdminList,
  getCommodityBidDetailRows,
  kindBadgeClass,
  kindLabel,
  normalizeCommodityBidAdminList,
} from '@/services/commoditybidIntegration';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';

function formatDateShort(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const StatusBadge = ({ status }) => {
  const s = String(status || '—').toLowerCase();
  const map = {
    pending: 'bg-amber-50 text-amber-700 ring-amber-600/20',
    open: 'bg-amber-50 text-amber-700 ring-amber-600/20',
    active: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
    closed: 'bg-slate-100 text-slate-600 ring-slate-500/10',
  };
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ring-1 ring-inset ${map[s] || 'bg-slate-50 text-slate-600 ring-slate-500/10'}`}
    >
      {status || '—'}
    </span>
  );
};

function StatPill({ label, value, accent }) {
  return (
    <div className={`rounded-xl border px-4 py-3 ${accent}`}>
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <p className="font-heading mt-0.5 text-2xl font-bold text-slate-900">{value}</p>
    </div>
  );
}

function DetailChip({ label, value }) {
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-lg bg-slate-50 px-2.5 py-1 text-xs text-slate-600 ring-1 ring-slate-200/80">
      <span className="shrink-0 font-medium text-slate-400">{label}</span>
      <span className="truncate text-slate-800">{value}</span>
    </span>
  );
}

function CommodityBidRecordCard({ item, expanded, onToggle }) {
  const detailRows = getCommodityBidDetailRows(item);
  const preview = detailRows.slice(0, 5);

  return (
    <article
      className="rounded-xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md"
      data-testid={`admin-cb-card-${item.kind}-${item.id}`}
    >
      <div className="p-5 sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 flex-1 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`inline-flex shrink-0 items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold ${kindBadgeClass(item.kind)}`}
              >
                {item.typeLabel || kindLabel(item.kind)}
              </span>
              <StatusBadge status={item.status} />
            </div>

            <div>
              <h3 className="font-heading text-lg font-semibold text-slate-900 sm:text-xl">{item.title}</h3>
              {item.supplierName ? (
                <p className="mt-1 text-sm text-slate-600">
                  Supplier: <span className="font-medium">{item.supplierName}</span>
                </p>
              ) : null}
            </div>

            {!expanded && preview.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {preview.map(({ label, value }) => (
                  <DetailChip key={label} label={label} value={value} />
                ))}
                {detailRows.length > preview.length ? (
                  <span className="self-center text-xs text-slate-400">
                    +{detailRows.length - preview.length} more
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="flex shrink-0 flex-col gap-3 sm:flex-row lg:flex-col lg:items-end lg:text-right">
            <div className="rounded-lg bg-slate-50 px-3 py-2.5 ring-1 ring-slate-100 lg:min-w-[200px]">
              <p className="text-sm font-semibold text-slate-900">{item.customerName}</p>
              <p className="mt-0.5 break-all text-xs text-slate-500">{item.customerEmail}</p>
            </div>
            <p className="text-xs text-slate-400 tabular-nums">{formatDateShort(item.createdAt)}</p>
          </div>
        </div>

        {expanded && detailRows.length > 0 ? (
          <dl className="mt-5 grid grid-cols-1 gap-4 border-t border-slate-100 pt-5 sm:grid-cols-2 lg:grid-cols-3">
            {detailRows.map(({ label, value }) => (
              <div key={label} className="min-w-0">
                <dt className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</dt>
                <dd className="mt-1 text-sm font-medium text-slate-800 break-words">{value}</dd>
              </div>
            ))}
          </dl>
        ) : null}

        {detailRows.length > 0 ? (
          <button
            type="button"
            onClick={onToggle}
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-orange-600 hover:text-orange-700"
          >
            {expanded ? (
              <>
                <ChevronUp className="h-4 w-4" />
                Hide details
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4" />
                View all details
              </>
            )}
          </button>
        ) : null}
      </div>
    </article>
  );
}

/** @param {{ hideHeader?: boolean }} props */
export default function CommodityBidAdminSection({ hideHeader = false }) {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({
    total: 0,
    production_request: 0,
    bid: 0,
    offer: 0,
    customersScanned: 0,
  });
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [expandedKey, setExpandedKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await getCommodityBidAdminList();
        if (cancelled) return;
        const normalized = normalizeCommodityBidAdminList(res.data);
        setItems(normalized.items);
        setCounts(normalized.counts);
        setNote(normalized.note);
      } catch (e) {
        if (!cancelled) {
          const detail = e?.response?.data?.detail;
          setError(typeof detail === 'string' ? detail : 'CommodityBid data could not be loaded.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((item) => {
      if (typeFilter !== 'all' && item.kind !== typeFilter) return false;
      if (!q) return true;
      const hay = [item.title, item.customerEmail, item.customerName, item.status, item.typeLabel]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [items, typeFilter, search]);

  return (
    <div className="space-y-5" data-testid="admin-commoditybid-section">
      {!hideHeader ? (
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-orange-500" />
          <div>
            <h2 className="font-heading text-base font-semibold text-slate-900">CommodityBid (live)</h2>
            <p className="text-xs text-slate-500">Synced from CommodityBid per customer email</p>
          </div>
        </div>
      ) : null}

      {!loading && !error ? (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatPill label="Total records" value={counts.total ?? 0} accent="border-slate-200 bg-white" />
          <StatPill
            label="Production requests"
            value={counts.production_request ?? 0}
            accent="border-orange-100 bg-orange-50/50"
          />
          <StatPill label="Supplier bids" value={counts.bid ?? 0} accent="border-amber-100 bg-amber-50/50" />
          <StatPill
            label="Customers scanned"
            value={counts.customersScanned ?? '—'}
            accent="border-slate-200 bg-white"
          />
        </div>
      ) : null}

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1 sm:max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search customer, product, status…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="admin-commoditybid-search"
            />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-full sm:w-48" data-testid="admin-commoditybid-type-filter">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="production_request">Production requests</SelectItem>
              <SelectItem value="bid">Supplier bids</SelectItem>
              <SelectItem value="offer">Supplier offers</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {note && !error && !loading ? (
          <p className="mt-3 flex items-start gap-2 text-xs text-slate-500">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
            <span>{note}</span>
          </p>
        ) : null}
      </div>

      {loading ? (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-slate-200 bg-white py-16">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
          <p className="text-sm text-slate-500">Loading CommodityBid records…</p>
        </div>
      ) : error ? (
        <div className="rounded-xl border border-red-100 bg-red-50 px-5 py-8 text-center text-sm text-red-700">
          {error}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 px-6 py-16 text-center">
          <Package className="mx-auto mb-3 h-10 w-10 text-slate-300" />
          <p className="font-medium text-slate-600">
            {items.length === 0 ? 'No CommodityBid records yet' : 'No records match your filters'}
          </p>
          <p className="mt-1 text-sm text-slate-400">
            Records appear when customers create bids on CommodityBid with their portal login email.
          </p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="admin-commoditybid-list">
          {filtered.map((item) => {
            const rowKey = `${item.kind}-${item.id}-${item.customerEmail}`;
            return (
              <CommodityBidRecordCard
                key={rowKey}
                item={item}
                expanded={expandedKey === rowKey}
                onToggle={() => setExpandedKey(expandedKey === rowKey ? null : rowKey)}
              />
            );
          })}
          <p className="text-center text-xs text-slate-400">
            Showing {filtered.length} of {items.length} records
          </p>
        </div>
      )}
    </div>
  );
}
